/**
 * Operational and Business Metrics saved-view window controls.
 */
import { formatInteger } from "./format.js";
import { labelize } from "./dashboard-html.js";
import { authenticatedFetch, getDashboardOperator } from "./dashboard-auth.js";
import { leadFactorySavedViewScopes, leadFactorySavedViewsUrl } from "./dashboard-urls.js";
import {
  getLeadFactoryMetricsDays,
  normalizedLimit,
  setLeadFactoryMetricsDays,
  setSavedViewFilters,
  setSavedViewUrl
} from "./dashboard-saved-view-state.js";
import {
  contactWorkDefaultCallDailyCapacity,
  contactWorkDefaultLetterDailyCapacity,
  contactWorkDefaultPlanTargetDays,
  contactWorkDefaultResearchDailyCapacity,
  normalizedContactWorkDailyCapacity,
  normalizedContactWorkOperatorRoster,
  normalizedContactWorkTargetDays
} from "./dashboard-contact-work.js";
import { copySavedViewLink, showSavedViewManager } from "./dashboard-saved-view-actions.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";
import type { LeadFactorySavedView } from "./dashboard-lead-factory-types.js";

type LeadFactorySavedViewScope = (typeof leadFactorySavedViewScopes)[number];
const leadFactoryBusinessMetricsScope: LeadFactorySavedViewScope = "business_metrics";
const leadFactorySavedViewVisibilities = ["team", "private"] as const;
type LeadFactorySavedViewVisibility = (typeof leadFactorySavedViewVisibilities)[number];

export function bindBusinessMetricsWindowControls(statusElement: HTMLElement, actions: DashboardActionHost): void {
  document.querySelectorAll<HTMLButtonElement>("[data-business-metrics-days]").forEach((button) => {
    button.addEventListener("click", () => {
      const days = Number(button.dataset.businessMetricsDays ?? 14);
      setLeadFactoryMetricsDays(days);
      statusElement.textContent = `Loading ${formatInteger(days)} day business metrics...`;
      actions.refresh();
    });
  });
  document.querySelectorAll<HTMLSelectElement>("[data-business-metrics-saved-view]").forEach((select) => {
    select.addEventListener("change", () => {
      const option = select.selectedOptions[0];
      const days = Number(option?.dataset.savedViewDays);
      if (!option || !Number.isFinite(days)) return;
      if (option.value) setSavedViewUrl(leadFactoryBusinessMetricsScope, option.value);
      setLeadFactoryMetricsDays(days);
      statusElement.textContent = `Loading saved ${formatInteger(days)} day business metrics view...`;
      actions.refresh();
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-business-metrics-share-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const select = button
        .closest<HTMLFormElement>("[data-business-metrics-saved-view-form]")
        ?.querySelector<HTMLSelectElement>("[data-business-metrics-saved-view]");
      void copySavedViewLink(leadFactoryBusinessMetricsScope, select?.value ?? "", statusElement);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-business-metrics-history-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const select = button
        .closest<HTMLFormElement>("[data-business-metrics-saved-view-form]")
        ?.querySelector<HTMLSelectElement>("[data-business-metrics-saved-view]");
      void showSavedViewManager(select?.value ?? "", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-business-metrics-rename-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const select = button
        .closest<HTMLFormElement>("[data-business-metrics-saved-view-form]")
        ?.querySelector<HTMLSelectElement>("[data-business-metrics-saved-view]");
      void showSavedViewManager(select?.value ?? "", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-business-metrics-delete-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const select = button
        .closest<HTMLFormElement>("[data-business-metrics-saved-view-form]")
        ?.querySelector<HTMLSelectElement>("[data-business-metrics-saved-view]");
      void showSavedViewManager(select?.value ?? "", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-business-metrics-grant-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const select = button
        .closest<HTMLFormElement>("[data-business-metrics-saved-view-form]")
        ?.querySelector<HTMLSelectElement>("[data-business-metrics-saved-view]");
      void showSavedViewManager(select?.value ?? "", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-business-metrics-revoke-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const select = button
        .closest<HTMLFormElement>("[data-business-metrics-saved-view-form]")
        ?.querySelector<HTMLSelectElement>("[data-business-metrics-saved-view]");
      void showSavedViewManager(select?.value ?? "", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-business-metrics-saved-view-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const name = form.querySelector<HTMLInputElement>("[data-business-metrics-saved-view-name]")?.value.trim();
      const visibility = savedViewVisibilityFromForm(form);
      if (!name) {
        statusElement.textContent = "Saved view name is required.";
        return;
      }
      void saveBusinessMetricsView(name, getLeadFactoryMetricsDays(), visibility, statusElement, actions);
    });
  });
}

export function savedViewVisibilityFromForm(form: HTMLFormElement): LeadFactorySavedViewVisibility {
  const value =
    form.querySelector<HTMLSelectElement>("[data-business-metrics-saved-view-visibility]")?.value ??
    form.querySelector<HTMLSelectElement>("[data-operational-saved-view-visibility]")?.value ??
    "team";
  return leadFactorySavedViewVisibilities.includes(value as LeadFactorySavedViewVisibility) ? (value as LeadFactorySavedViewVisibility) : "team";
}

export async function saveBusinessMetricsView(
  name: string,
  days: number,
  visibility: LeadFactorySavedViewVisibility,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  statusElement.textContent = `Saving ${formatInteger(days)} day business metrics view...`;
  try {
    const response = await authenticatedFetch(leadFactorySavedViewsUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        scope: leadFactoryBusinessMetricsScope,
        name,
        filters: { days },
        visibility
      })
    });
    if (!response.ok) throw new Error(`Saved view failed: ${response.status}`);
    const saved = (await response.json()) as LeadFactorySavedView;
    setSavedViewUrl(leadFactoryBusinessMetricsScope, saved.id);
    statusElement.textContent = "Saved metrics view and copied link target.";
    await Promise.resolve(actions.refresh());
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Saved view failed.";
  }
}

export function bindOperationalSavedViewControls(statusElement: HTMLElement, actions: DashboardActionHost): void {
  document.querySelectorAll<HTMLSelectElement>("[data-operational-saved-view]").forEach((select) => {
    select.addEventListener("change", () => {
      const scope = select.dataset.operationalSavedView as LeadFactorySavedViewScope | undefined;
      if (!scope) return;
      const option = select.selectedOptions[0];
      const filtersJson = option?.dataset.savedViewFilters;
      if (!filtersJson) return;
      const filters = parseSavedViewFiltersFromAttribute(filtersJson);
      setSavedViewFilters(scope, filters);
      if (option.value) setSavedViewUrl(scope, option.value);
      statusElement.textContent = `Loading ${labelize(scope)} saved view...`;
      actions.refresh();
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-operational-share-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const scope = button.dataset.operationalShareView as LeadFactorySavedViewScope | undefined;
      const select = button.closest<HTMLFormElement>("[data-operational-saved-view-form]")?.querySelector<HTMLSelectElement>(
        "[data-operational-saved-view]"
      );
      if (!scope) return;
      void copySavedViewLink(scope, select?.value ?? "", statusElement);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-operational-history-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const select = button.closest<HTMLFormElement>("[data-operational-saved-view-form]")?.querySelector<HTMLSelectElement>(
        "[data-operational-saved-view]"
      );
      void showSavedViewManager(select?.value ?? "", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-operational-rename-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const select = button.closest<HTMLFormElement>("[data-operational-saved-view-form]")?.querySelector<HTMLSelectElement>(
        "[data-operational-saved-view]"
      );
      void showSavedViewManager(select?.value ?? "", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-operational-delete-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const select = button.closest<HTMLFormElement>("[data-operational-saved-view-form]")?.querySelector<HTMLSelectElement>(
        "[data-operational-saved-view]"
      );
      void showSavedViewManager(select?.value ?? "", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-operational-grant-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const select = button.closest<HTMLFormElement>("[data-operational-saved-view-form]")?.querySelector<HTMLSelectElement>(
        "[data-operational-saved-view]"
      );
      void showSavedViewManager(select?.value ?? "", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-operational-revoke-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const select = button.closest<HTMLFormElement>("[data-operational-saved-view-form]")?.querySelector<HTMLSelectElement>(
        "[data-operational-saved-view]"
      );
      void showSavedViewManager(select?.value ?? "", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-contact-work-my-work]").forEach((button) => {
    button.addEventListener("click", () => {
      const form = button.closest<HTMLFormElement>("[data-operational-saved-view-form]");
      const operator = getDashboardOperator();
      if (!form || !operator) {
        statusElement.textContent = "Set a dashboard operator before opening My Work.";
        return;
      }
      const assignedToInput = form.querySelector<HTMLInputElement>('[data-operational-filter="assignedTo"]');
      if (assignedToInput) assignedToInput.value = operator;
      setSavedViewFilters("contact_work", operationalFiltersFromForm("contact_work", form));
      statusElement.textContent = `Loading Contact Work assigned to ${operator}...`;
      actions.refresh();
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-operational-filter-apply]").forEach((button) => {
    button.addEventListener("click", () => {
      const scope = button.dataset.operationalFilterApply as LeadFactorySavedViewScope | undefined;
      const form = button.closest<HTMLFormElement>("[data-operational-saved-view-form]");
      if (!scope || !form) return;
      setSavedViewFilters(scope, operationalFiltersFromForm(scope, form));
      statusElement.textContent = `Applying ${labelize(scope)} filters...`;
      actions.refresh();
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-operational-saved-view-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const scope = form.dataset.operationalSavedViewForm as LeadFactorySavedViewScope | undefined;
      if (!scope) return;
      const name = form.querySelector<HTMLInputElement>("[data-operational-saved-view-name]")?.value.trim();
      if (!name) {
        statusElement.textContent = "Saved view name is required.";
        return;
      }
      const filters = operationalFiltersFromForm(scope, form);
      const visibility = savedViewVisibilityFromForm(form);
      setSavedViewFilters(scope, filters);
      void saveOperationalView(scope, name, filters, visibility, statusElement, actions);
    });
  });
}

export function parseSavedViewFiltersFromAttribute(value: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function operationalFiltersFromForm(scope: LeadFactorySavedViewScope, form: HTMLFormElement): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  form.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-operational-filter]").forEach((input) => {
    const key = input.dataset.operationalFilter;
    if (!key) return;
    values[key] =
      key === "limit"
        ? normalizedLimit(input.value, scope === "review_queue" ? 75 : 75, scope === "review_queue" ? 250 : 500)
        : scope === "contact_work" && key === "targetDays"
          ? normalizedContactWorkTargetDays(input.value)
          : scope === "contact_work" && key === "callDailyCapacity"
            ? normalizedContactWorkDailyCapacity(input.value, contactWorkDefaultCallDailyCapacity)
            : scope === "contact_work" && key === "letterDailyCapacity"
            ? normalizedContactWorkDailyCapacity(input.value, contactWorkDefaultLetterDailyCapacity)
            : scope === "contact_work" && key === "researchDailyCapacity"
              ? normalizedContactWorkDailyCapacity(input.value, contactWorkDefaultResearchDailyCapacity)
              : scope === "contact_work" && key.endsWith("OperatorRoster")
                ? normalizedContactWorkOperatorRoster(input.value)
          : input.value.trim();
  });
  if (scope === "contact_work" && !values.queue) values.queue = "all";
  if (scope === "contact_work" && !values.letterKind) values.letterKind = "all";
  if (scope === "contact_queue" && !values.scope) values.scope = "lead_supply";
  if (scope === "contact_queue" && values.excludeTest === undefined) values.excludeTest = "true";
  if (scope === "contact_work" && !values.scope) values.scope = "lead_supply";
  if (scope === "contact_work" && values.excludeTest === undefined) values.excludeTest = "true";
  if (scope === "contact_work" && values.actionableOnly === undefined) values.actionableOnly = "true";
  if (scope === "contact_work" && values.assignedTo === undefined) values.assignedTo = "";
  if (scope === "contact_work" && values.targetDays === undefined) values.targetDays = contactWorkDefaultPlanTargetDays;
  if (scope === "contact_work" && values.callDailyCapacity === undefined) values.callDailyCapacity = contactWorkDefaultCallDailyCapacity;
  if (scope === "contact_work" && values.letterDailyCapacity === undefined) values.letterDailyCapacity = contactWorkDefaultLetterDailyCapacity;
  if (scope === "contact_work" && values.researchDailyCapacity === undefined) values.researchDailyCapacity = contactWorkDefaultResearchDailyCapacity;
  if (scope === "contact_work" && values.callOperatorRoster === undefined) values.callOperatorRoster = "";
  if (scope === "contact_work" && values.letterOperatorRoster === undefined) values.letterOperatorRoster = "";
  if (scope === "contact_work" && values.researchOperatorRoster === undefined) values.researchOperatorRoster = "";
  if (scope === "acquisition_work" && !values.queue) values.queue = "all";
  if (scope === "asset_work" && !values.status) values.status = "assigned";
  return values;
}

export async function saveOperationalView(
  scope: LeadFactorySavedViewScope,
  name: string,
  filters: Record<string, unknown>,
  visibility: LeadFactorySavedViewVisibility,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  statusElement.textContent = `Saving ${labelize(scope)} view...`;
  try {
    const response = await authenticatedFetch(leadFactorySavedViewsUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ scope, name, filters, visibility })
    });
    if (!response.ok) throw new Error(`Saved view failed: ${response.status}`);
    const saved = (await response.json()) as LeadFactorySavedView;
    setSavedViewUrl(scope, saved.id);
    statusElement.textContent = `Saved ${labelize(scope)} view.`;
    await Promise.resolve(actions.refresh());
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Saved view failed.";
  }
}

